// FILE: src/pages/admin/AdminDailyHoursPage.jsx
// Daily learning hours (WP-7; MTO CD3.4b, 11.1.2; matrix 11.14): the time
// credited to each student on each Ontario day, core (chapters) and flex
// (homelinks) separate, and whether the day reached the 5-hour limit.
//   GET /api/admin/reports/daily-hours ?from&to (to included) [&userId] &format=json|csv
//   json → { from, to, timeZone, generatedAt,
//            rows: [{ userId, email, firstName, lastName, registrationNumber, day,
//                     coreMinutes, flexMinutes, unattributedMinutes, totalMinutes,
//                     capped, legacy, untimed }],
//            totals: { rows, students, coreMinutes, flexMinutes, unattributedMinutes, totalMinutes, cappedDays } }
//   csv  → daily-hours-<from>-to-<to>.csv (the server's file)
// The report is made when asked ("Show report"; each one is audited) and
// then kept in the URL (?from=&to=&userId=) so reload and Back show it again.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Link,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import fetchWithAuth from "../../utils/fetchWithAuth";
import { EmptyState, ErrorState, LoadingState, Section, StatCard, StatusChip } from "../../components/ui";
import { FigureGroup, ResponsiveTable } from "../../components/admin/AdminBits";
import { downloadFromApi } from "../../components/admin/download";
import { PAGINATION_SX } from "../../components/admin/AuditLogTable";
import { PhoneCard, PhoneList, useIsPhone } from "../../components/admin/PhoneList";
import { dayOrEmpty, formatDay, rangeProblem, torontoDay, torontoDaysAgo } from "../../components/admin/dates";
import { formatDateTime, formatNumber, personName } from "../../components/admin/format";
import { formatDuration } from "../../utils/timeFormat";
import { errorMessage } from "../../i18n/errors";
import { ExportsShell } from "./AdminExportsPage";

const ID_RE = /^[a-f0-9]{24}$/i;
const PAGE_SIZES = [25, 50, 100];
const mins = (m) => formatDuration(Math.round((Number(m) || 0) * 60));

export default function AdminDailyHoursPage() {
  const [params, setParams] = useSearchParams();
  const userId = ID_RE.test(params.get("userId") || "") ? params.get("userId") : "";
  const urlFrom = dayOrEmpty(params.get("from"));
  const urlTo = dayOrEmpty(params.get("to"));
  // A link with a student (from their page) shows the last 30 days at once.
  const asked = !!(urlFrom && urlTo) || !!userId;
  const from = urlFrom || torontoDaysAgo(30);
  const to = urlTo || torontoDay();

  const [draft, setDraft] = useState({ from, to });
  const [problem, setProblem] = useState("");
  const [state, setState] = useState({ report: null, loading: false, error: null });
  const [csv, setCsv] = useState({ busy: false, error: null, filename: null });
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(PAGE_SIZES[0]);
  const latest = useRef(0);
  const phone = useIsPhone();

  useEffect(() => setDraft({ from, to }), [from, to]);

  const query = useMemo(() => {
    const q = new URLSearchParams({ from, to });
    if (userId) q.set("userId", userId);
    return q.toString();
  }, [from, to, userId]);

  const load = useCallback(() => {
    const req = ++latest.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    setPage(0);
    fetchWithAuth(`/api/admin/reports/daily-hours?${query}&format=json`)
      .then((report) => {
        if (req === latest.current) setState({ report, loading: false, error: null });
      })
      .catch((error) => {
        if (req === latest.current) setState((s) => ({ ...s, loading: false, error: error || "INTERNAL_ERROR" }));
      });
  }, [query]);

  useEffect(() => {
    if (asked) load();
  }, [asked, load]);

  const apply = (e) => {
    e.preventDefault();
    const bad = rangeProblem(draft.from, draft.to);
    setProblem(bad);
    if (bad) return;
    const next = { from: draft.from, to: draft.to };
    if (userId) next.userId = userId;
    // Same dates again: make the report again.
    if (asked && draft.from === from && draft.to === to) load();
    else setParams(next);
  };

  const downloadCsv = async () => {
    setCsv({ busy: true, error: null, filename: null });
    try {
      const file = await downloadFromApi(`/api/admin/reports/daily-hours?${query}&format=csv`, {
        fallbackName: `daily-hours-${from}-to-${to}.csv`
      });
      setCsv({ busy: false, error: null, filename: file.filename });
    } catch (err) {
      setCsv({ busy: false, error: err || "INTERNAL_ERROR", filename: null });
    }
  };

  const { report, loading, error } = state;
  const rows = Array.isArray(report?.rows) ? report.rows : [];
  const totals = report?.totals || {};
  const shown = rows.slice(page * size, page * size + size);
  const student = userId && rows[0] ? personName(rows[0], "this student") : null;

  return (
    <ExportsShell
      title="Daily learning hours"
      description="Time credited to each student on each day (Ontario time), with core and flex hours separate (MTO 11.14). At most 5 hours count on any day."
    >
      <Stack spacing={2.5}>
        <Section id="hours-dates" title="Dates">
          <Box component="form" noValidate onSubmit={apply}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "flex-start" } }}>
              <TextField
                id="hours-from"
                label="From"
                type="date"
                value={draft.from}
                onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
                error={!!problem}
                required
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: draft.to || undefined } }}
              />
              <TextField
                id="hours-to"
                label="To"
                type="date"
                value={draft.to}
                onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
                error={!!problem}
                required
                helperText="Includes this day"
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: draft.from || undefined } }}
              />
              <Button type="submit" variant="contained" sx={{ mt: { sm: 1 } }} disabled={loading}>
                Show report
              </Button>
            </Stack>
            {problem && (
              <Alert severity="error" role="alert" sx={{ mt: 2 }}>
                {problem}
              </Alert>
            )}
          </Box>
          {userId && (
            <Stack direction="row" spacing={1.5} sx={{ mt: 2, alignItems: "center", flexWrap: "wrap", rowGap: 1 }}>
              <Typography sx={{ fontWeight: 600 }}>
                Only{" "}
                <Link component={RouterLink} to={`/admin/users/${userId}`}>
                  {student || "one student"}
                </Link>
              </Typography>
              <Button
                startIcon={<FilterAltOffOutlinedIcon />}
                onClick={() => setParams(asked ? { from, to } : {})}
              >
                Show every student
              </Button>
            </Stack>
          )}
        </Section>

        {!asked && !report ? (
          <EmptyState
            icon={CalendarMonthOutlinedIcon}
            title="Choose the dates, then Show report"
            description="The report lists every student with credited time on each day. Making a report is recorded in the audit log."
          />
        ) : report === null && error ? (
          <ErrorState error={error} title="We couldn't make the report" onRetry={load} />
        ) : report === null ? (
          <LoadingState variant="page" label="Making the report…" />
        ) : (
          <>
            {error && <ErrorState compact error={error} onRetry={load} />}
            <Box sx={{ height: 8 }}>{loading && <LinearProgress aria-label="Updating the report" />}</Box>
            <FigureGroup
              id="hours-totals"
              title={`${formatDay(report.from)} to ${formatDay(report.to)}`}
              description={report.generatedAt ? `Report made ${formatDateTime(report.generatedAt)}.` : undefined}
              columns={4}
            >
              <StatCard
                icon={PeopleOutlineIcon}
                label="Students with time"
                value={formatNumber(totals.students ?? 0)}
                note={`${formatNumber(totals.rows ?? rows.length)} student-days`}
                testId="hours-students"
              />
              <StatCard icon={MenuBookOutlinedIcon} label="Core (chapters)" value={mins(totals.coreMinutes)} testId="hours-core" />
              <StatCard
                icon={HomeWorkOutlinedIcon}
                label="Flex (homelinks)"
                value={mins(totals.flexMinutes)}
                note={Number(totals.unattributedMinutes) > 0 ? `${mins(totals.unattributedMinutes)} not linked to a lesson` : undefined}
                testId="hours-flex"
              />
              <StatCard
                icon={FlagOutlinedIcon}
                label="Days at the 5-hour limit"
                value={formatNumber(totals.cappedDays ?? 0)}
                note={`${mins(totals.totalMinutes)} in total`}
                testId="hours-capped"
              />
            </FigureGroup>

            <Section
              id="hours-days"
              title="Per student, per day"
              action={
                <Button
                  variant="outlined"
                  onClick={downloadCsv}
                  disabled={csv.busy}
                  startIcon={csv.busy ? <CircularProgress size={18} color="inherit" aria-hidden /> : <FileDownloadOutlinedIcon />}
                >
                  {csv.busy ? "Preparing…" : "Download CSV"}
                </Button>
              }
              contentSx={{ p: { xs: 2, sm: 3 } }}
            >
              {csv.error && (
                <Alert severity="error" role="alert" sx={{ mb: 2 }}>
                  <AlertTitle>We couldn't make the CSV file</AlertTitle>
                  {errorMessage(csv.error)}
                </Alert>
              )}
              {csv.filename && (
                <Alert severity="success" role="status" sx={{ mb: 2 }}>
                  Saved as <strong>{csv.filename}</strong>.
                </Alert>
              )}
              {rows.length === 0 ? (
                <EmptyState
                  compact
                  title="No credited time on these days"
                  description="Nobody had learning time credited between these dates. Try a wider range."
                />
              ) : (
                <>
                  {phone ? (
                    <PhoneList label="Credited learning time per student per day">
                      {shown.map((r) => (
                        <PhoneCard key={`${r.userId}-${r.day}`}>
                          <Box>
                            <Typography sx={{ fontWeight: 650 }}>{formatDay(r.day)}</Typography>
                            <Link component={RouterLink} to={`/admin/users/${r.userId}`}>
                              {personName(r, r.email || "Student")}
                            </Link>
                            {r.registrationNumber && <Typography sx={{ color: "text.secondary" }}>{r.registrationNumber}</Typography>}
                          </Box>
                          <Typography>
                            Core {mins(r.coreMinutes)} · Flex {mins(r.flexMinutes)}
                            {Number(r.unattributedMinutes) > 0 ? ` · Not linked ${mins(r.unattributedMinutes)}` : ""}
                          </Typography>
                          <Stack direction="row" sx={{ alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                            <Typography sx={{ fontWeight: 650 }}>Total {mins(r.totalMinutes)}</Typography>
                            {r.capped && <StatusChip tone="warning" icon={<FlagOutlinedIcon />} label="Reached the 5-hour limit" />}
                          </Stack>
                        </PhoneCard>
                      ))}
                    </PhoneList>
                  ) : (
                    <ResponsiveTable label="Credited learning time per student per day" minWidth={900}>
                      <TableHead>
                        <TableRow>
                          <TableCell component="th" scope="col">
                            Day
                          </TableCell>
                          <TableCell component="th" scope="col">
                            Student
                          </TableCell>
                          <TableCell component="th" scope="col" align="right">
                            Core
                          </TableCell>
                          <TableCell component="th" scope="col" align="right">
                            Flex
                          </TableCell>
                          <TableCell component="th" scope="col" align="right">
                            Not linked
                          </TableCell>
                          <TableCell component="th" scope="col" align="right">
                            Total
                          </TableCell>
                          <TableCell component="th" scope="col">
                            5-hour limit
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {shown.map((r) => {
                          const name = personName(r, r.email || "Student");
                          return (
                            <TableRow key={`${r.userId}-${r.day}`}>
                              <TableCell component="th" scope="row" sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                                {formatDay(r.day)}
                              </TableCell>
                              <TableCell>
                                <Link component={RouterLink} to={`/admin/users/${r.userId}`}>
                                  {name}
                                </Link>
                                {r.registrationNumber && (
                                  <Typography sx={{ color: "text.secondary" }}>{r.registrationNumber}</Typography>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                {mins(r.coreMinutes)}
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                {mins(r.flexMinutes)}
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                {mins(r.unattributedMinutes)}
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                                {mins(r.totalMinutes)}
                              </TableCell>
                              <TableCell>
                                {r.capped ? (
                                  <StatusChip tone="warning" icon={<FlagOutlinedIcon />} label="Reached" />
                                ) : (
                                  <Typography component="span" sx={{ color: "text.secondary" }}>
                                    Not reached
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </ResponsiveTable>
                  )}
                  <TablePagination
                    component="div"
                    count={rows.length}
                    page={Math.min(page, Math.max(0, Math.ceil(rows.length / size) - 1))}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={size}
                    onRowsPerPageChange={(e) => {
                      setSize(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={PAGE_SIZES}
                    labelRowsPerPage="Rows per page"
                    labelDisplayedRows={({ from: a, to: b, count }) => `${a}–${b} of ${count}`}
                    getItemAriaLabel={(type) => (type === "next" ? "Next page" : type === "previous" ? "Previous page" : `Go to ${type} page`)}
                    sx={PAGINATION_SX}
                  />
                </>
              )}
            </Section>
          </>
        )}
      </Stack>
    </ExportsShell>
  );
}
